import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { api } from "../lib/api";

const ModeContext = createContext(null);

export const ModeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("salon_mode") || "null");
      if (saved?.token && saved?.mode) {
        api.defaults.headers.common["X-Mode-Token"] = saved.token;
        return saved.mode;
      }
    } catch (e) {
      console.warn("Could not restore saved mode:", e);
    }
    return null;
  });
  const [privacy, setPrivacy] = useState(() => localStorage.getItem("salon_privacy") === "1");

  const togglePrivacy = useCallback(() =>
    setPrivacy((p) => {
      const next = !p;
      localStorage.setItem("salon_privacy", next ? "1" : "0");
      return next;
    }), []);

  const applyMode = useCallback((m, token) => {
    localStorage.setItem("salon_mode", JSON.stringify({ mode: m, token }));
    api.defaults.headers.common["X-Mode-Token"] = token;
    setMode(m);
  }, []);

  const unlock = useCallback(async (pin) => {
    const { data } = await api.post("/mode/verify", { pin });
    applyMode(data.mode, data.mode_token);
    return data.mode;
  }, [applyMode]);

  const recover = useCallback(async (recovery_key) => {
    const { data } = await api.post("/mode/recover", { recovery_key });
    applyMode(data.mode, data.mode_token);
    return data.mode;
  }, [applyMode]);

  const switchMode = useCallback(() => {
    localStorage.removeItem("salon_mode");
    localStorage.setItem("salon_privacy", "0");
    delete api.defaults.headers.common["X-Mode-Token"];
    setPrivacy(false);
    setMode(null);
  }, []);

  const viewMode = mode === "ceo" && privacy ? "manager" : mode;

  // Mode veil token is backend-signed and role-scoped (not the auth credential, which lives in an httpOnly cookie).
  const value = useMemo(
    () => ({ mode, viewMode, privacy, togglePrivacy, unlock, recover, switchMode }),
    [mode, viewMode, privacy, togglePrivacy, unlock, recover, switchMode]
  );

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
};

export const useMode = () => useContext(ModeContext);
